import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addToCart,
  registerCheckout,
  fetchCheckouts,
  fetchProductDetail,
  fetchUserAssetBalances,
  selectIsUserLoggedIn,
  selectUserInfo,
  selectWithcookieData,
  selectProductDetailState,
  selectUserAssetBalances,
} from '../../../redux/WithcookieSlice';
import { showToast } from '../../common/Toast';
import { useTranslation } from '../../../redux/TranslationHelper';
import { CustomMembershipSignupLogicReturn } from './CustomMembershipSignup.types';

/**
 * 커스텀 멤버십 가입 로직 훅
 */
export const useCustomMembershipSignupLogic = (props: any): CustomMembershipSignupLogicReturn => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = props?.navigate;
  
  // Redux 상태
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const userInfo = useSelector(selectUserInfo);
  const productDetailState = useSelector(selectProductDetailState);
  const withcookieData = useSelector(selectWithcookieData);
  const userAssetBalances = useSelector(selectUserAssetBalances);
  
  // Props에서 필요한 값 추출
  const productId = typeof props?.options?.productId === 'string' 
    ? parseInt(props.options.productId, 10) || 0 
    : props?.options?.productId || 0;
  
  const {
    askAmount = true,
    askQuantity = true,
    askAdditionalProducts = false,
    minAmount = 0,
    maxAmount = 100000000,
    minQuantity = 1,
    maxQuantity = 100
  } = props?.options || {};
  
  // 로컬 상태
  const [isRegistered, setIsRegistered] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const [selectedAmount, setSelectedAmount] = useState<number>(minAmount);
  const [selectedQuantity, setSelectedQuantity] = useState<number>(minQuantity);
  const [selectedPriceId, setSelectedPriceId] = useState<string>('');
  const [productInfo, setProductInfo] = useState<any>(null);
  const [additionalProducts, setAdditionalProducts] = useState<any[]>([]);
  const [selectedAdditionalProducts, setSelectedAdditionalProducts] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [assetBalance, setAssetBalance] = useState<number>(0);
  
  // 유틸리티 함수
  const formatNumber = useCallback((value: string): string => {
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue === '') return '';
    const num = parseInt(cleanValue, 10);
    return num.toLocaleString('en-US');
  }, []);
  
  // 사용자의 멤버십 상태 확인
  const checkMembershipStatus = useCallback(async () => {
    if (isUserLoggedIn && productId > 0) {
      try {
        setIsChecking(true);
        const response = await dispatch(fetchCheckouts({ page: 1, take: 100 }) as any).unwrap();
        const checkouts = response?.checkouts || response || [];
        
        if (Array.isArray(checkouts)) {
          const hasMembership = checkouts.some((checkout: any) => {
            const products = checkout.products || [];
            return products.some((product: any) => 
              product.id === productId && 
              (checkout.status === 'checked_out' || checkout.status === 'active')
            );
          });
          
          setIsRegistered(hasMembership);
        }
      } catch (error) {
        console.error('Error checking membership status:', error);
      } finally {
        setIsChecking(false);
      }
    } else {
      setIsChecking(false);
    }
  }, [dispatch, isUserLoggedIn, productId]);
  
  // 상품 정보 가져오기
  const fetchProductInfo = useCallback(async () => {
    if (productId > 0) {
      try {
        const result = await dispatch(fetchProductDetail(productId) as any).unwrap();
        setProductInfo(result);
        
        // 첫 번째 가격을 기본값으로 설정
        if (result?.prices && result.prices.length > 0) {
          const firstPrice = result.prices[0];
          setSelectedPriceId(firstPrice.id.toString());
          
          // 금액이 설정되어 있으면 사용
          if (firstPrice.unit_amount_decimal) {
            setSelectedAmount(parseFloat(firstPrice.unit_amount_decimal));
          }
        }
      } catch (error) {
        console.error('Error fetching product info:', error);
      }
    }
  }, [dispatch, productId]);
  
  // 사용자 자산 잔액 가져오기
  const fetchAssetBalance = useCallback(async () => {
    if (isUserLoggedIn) {
      try {
        await dispatch(fetchUserAssetBalances() as any);
      } catch (error) {
        console.error('Error fetching asset balance:', error);
      }
    }
  }, [dispatch, isUserLoggedIn]);
  
  // 추가 상품 정보 가져오기 (필요한 경우)
  const fetchAdditionalProducts = useCallback(async () => {
    if (askAdditionalProducts) {
      // 추가 상품 목록을 가져오는 로직
      // 실제 구현은 API에 따라 달라질 수 있음
      try {
        // TODO: 추가 상품 목록 API 호출
        setAdditionalProducts([]);
      } catch (error) {
        console.error('Error fetching additional products:', error);
      }
    }
  }, [askAdditionalProducts]);
  
  // 금액 변경 처리
  const handleAmountChange = useCallback((value: string) => {
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue === '') {
      setSelectedAmount(0);
      return;
    }
    
    const numValue = parseInt(cleanValue, 10);
    if (!isNaN(numValue)) {
      const clampedValue = Math.min(Math.max(numValue, minAmount), maxAmount);
      setSelectedAmount(clampedValue);
    }
  }, [minAmount, maxAmount]);
  
  // 수량 변경 처리
  const handleQuantityChange = useCallback((value: string) => {
    const cleanValue = value.replace(/[^0-9]/g, '');
    if (cleanValue === '') {
      setSelectedQuantity(minQuantity);
      return;
    }
    
    const numValue = parseInt(cleanValue, 10);
    if (!isNaN(numValue)) {
      const clampedValue = Math.min(Math.max(numValue, minQuantity), maxQuantity);
      setSelectedQuantity(clampedValue);
    }
  }, [minQuantity, maxQuantity]);
  
  // 추가 상품 선택 처리
  const handleAdditionalProductToggle = useCallback((productId: string) => {
    setSelectedAdditionalProducts(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  }, []);
  
  // 멤버십 가입 처리
  const handleMembershipSignup = useCallback(async () => {
    if (!isUserLoggedIn) {
      showToast(t('로그인이 필요합니다.'), 'error');
      if (navigate) {
        navigate('/login');
      }
      return;
    }
    
    if (productId <= 0) {
      showToast(t('유효하지 않은 상품입니다.'), 'error');
      return;
    }
    
    if (askAmount && selectedAmount <= 0) {
      showToast(t('금액을 입력해주세요.'), 'error');
      return;
    }
    
    if (askQuantity && selectedQuantity <= 0) {
      showToast(t('수량을 입력해주세요.'), 'error');
      return;
    }
    
    try {
      setLoading(true);
      
      // 1. 장바구니에 추가
      const cartItems = [{
        productId: productId,
        quantity: selectedQuantity,
        priceId: selectedPriceId,
        customAmount: askAmount ? selectedAmount : undefined
      }];
      
      // 추가 상품이 있는 경우
      if (selectedAdditionalProducts.length > 0) {
        selectedAdditionalProducts.forEach(additionalProductId => {
          cartItems.push({
            productId: parseInt(additionalProductId, 10),
            quantity: 1,
            priceId: '', // 추가 상품의 기본 가격 사용
            customAmount: undefined
          });
        });
      }
      
      // 장바구니에 추가
      for (const item of cartItems) {
        const cartParams = {
          product_id: item.productId,
          count: item.quantity,
          priceId: item.priceId,
          customAmount: item.customAmount
        };
        await dispatch(addToCart(cartParams) as any).unwrap();
      }
      
      // 2. 체크아웃 등록 - 간단한 형태로 변경
      const checkoutData = {
        productId: productId,
        priceId: selectedPriceId,
        quantity: 1,
        customAmount: askAmount ? selectedAmount : undefined,
        additionalProducts: selectedAdditionalProducts
      };
      
      // TODO: 체크아웃 로직 구현 필요
      // const checkoutResult = await dispatch(registerCheckout(checkoutData) as any).unwrap();
      
      // 임시로 성공 처리
      if (selectedAmount === 0) {
        // 무료 상품인 경우
        setIsRegistered(true);
        showToast(t('멤버십 가입이 완료되었습니다.'), 'success');
      } else {
        // 유료 상품인 경우 - 장바구니에 추가 완료
        showToast(t('장바구니에 추가되었습니다.'), 'success');
      }
    } catch (error) {
      console.error('Error during membership signup:', error);
      showToast(t('멤버십 가입 중 오류가 발생했습니다.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [
    dispatch, 
    isUserLoggedIn, 
    productId, 
    selectedAmount, 
    selectedQuantity, 
    selectedPriceId,
    selectedAdditionalProducts,
    askAmount,
    askQuantity,
    t, 
    navigate
  ]);
  
  // Effect: 컴포넌트 마운트 시
  useEffect(() => {
    checkMembershipStatus();
    fetchProductInfo();
    fetchAssetBalance();
    fetchAdditionalProducts();
  }, [checkMembershipStatus, fetchProductInfo, fetchAssetBalance, fetchAdditionalProducts]);
  
  // Effect: 자산 잔액 업데이트
  useEffect(() => {
    if (userAssetBalances && userAssetBalances.balances && userAssetBalances.balances.length > 0) {
      const mainAsset = userAssetBalances.balances.find((asset: any) => asset.is_main);
      if (mainAsset) {
        setAssetBalance((mainAsset as any).balance || 0);
      }
    }
  }, [userAssetBalances]);
  
  return {
    data: {
      isRegistered,
      isChecking,
      isUserLoggedIn,
      productInfo,
      selectedAmount,
      selectedQuantity,
      selectedPriceId,
      additionalProducts,
      selectedAdditionalProducts,
      loading,
      assetBalance,
      theme: withcookieData?.theme || {},
      askAmount,
      askQuantity,
      askAdditionalProducts,
      minAmount,
      maxAmount,
      minQuantity,
      maxQuantity
    },
    actions: {
      handleMembershipSignup,
      handleAmountChange,
      handleQuantityChange,
      handleAdditionalProductToggle,
      formatNumber
    }
  };
};